/**
 * ÉTAT DU STOCK — l'écran `stock2` de la maquette : ce qu'on a en rayon, ce que ça vaut, et la seule
 * façon de le corriger.
 *
 * ⚠️ PORTAGE VERBATIM DE LA MAQUETTE (`design/`, écran `stock2`), comme `ecarts.tsx` : mêmes
 * boîtes, mêmes tailles, mêmes teintes, tracés SVG repris tels quels. Deux versions précédentes ont
 * « traduit » la maquette au lieu de la copier — classes utilitaires inexistantes d'abord, composants
 * de charte ensuite — et l'écran ne ressemblait à rien de validé. La maquette est le dessin ; on le
 * REPRODUIT. Les seules libertés sont imposées par les données réelles, et chacune est marquée ⚠️.
 *
 * Ce que la page SERT, dans les QUATRE vues de la maquette :
 *  • Stock local — la photo par déclinaison, filtrée, triée, valorisée au PMP rejoué, corrigeable
 *    ligne à ligne (− / saisie / +) ;
 *  • Grille de tailles — le même stock en matrice modèle × taille, corrigeable case par case ;
 *  • Croisé — deux axes et une mesure, agrégés par le SERVEUR, avec des vues nommées partagées ;
 *  • Stock distant — la grille comparée site par site, en lecture seule.
 * Plus le grand livre d'une référence en panneau latéral, et la correction EN LOT (motif obligatoire,
 * précision libre, un mouvement écrit par ligne).
 *
 * ⚠️ CORRIGER N'ÉCRASE RIEN. Le chiffre affiché est la somme d'un grand livre append-only : une
 * correction ajoute une ligne, elle ne retouche jamais les anciennes. C'est écrit À L'ÉCRAN —
 * l'opérateur doit savoir que son geste laisse une trace, pas croire qu'il « remet le bon chiffre ».
 *
 * ⚠️ QUATRE VUES, UN SEUL FILTRE, construit ici et passé aux vues : deux constructions finiraient par
 * diverger sur un paramètre, et personne ne saurait lequel des deux totaux est le bon.
 */
import { exporterCsv } from './export-csv';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copyable, FilterToolbar, KpiRow, Picker, Segmented, showToast, Trans, type KpiItem, useLatest, useMediaMax, useOverlayPanel } from '@orgazone/ui-web';
import { RAIL_BREAKPOINT, TABLE } from '@orgazone/ui-tokens';
import { currentCurrency, formatMoney, toInputValue } from '@orgazone/money-format';
import {
  api, STOCK_PAGE_SIZE, type StockCorrectionReason, type StockLedgerLine, type StockPage, type StockQuery,
  type StockRow, type StockSortKey,
} from '../api';
import { useStoreScope } from '../store-context';
import { fieldLabel } from '../vocabulary';
import { familyDot, GrilleDesTailles, StockDistant, TableauCroise, Trait } from './stock-views';
import { NATURES, RAISON } from './mouvements';
import { rescale } from '@orgazone/core-money';
import { key, nombre, t, type Translatable } from '../i18n';

/** Les quatre présentations, dans l'ordre de la maquette. */
const VUES = [
  { v: 'table', l: key('Stock local') },
  { v: 'grille', l: key('Grille de tailles') },
  { v: 'pivot', l: key('Croisé') },
  { v: 'distant', l: key('Stock distant') },
] as const;
type Vue = (typeof VUES)[number]['v'];

/**
 * Les colonnes facultatives de la liste — clés et LARGEURS de la maquette (`stGridT`) ; leur mot vient
 * de `columnLabel`.
 * ⚠️ MASQUABLES, PAS SUPPRIMABLES : la référence et la quantité restent toujours là. Une liste de
 * stock sans quantité n'est plus une liste de stock — offrir de la masquer serait offrir de casser
 * l'écran (la maquette verrouille « Article » et « Stock » dans son panneau, on fait pareil).
 */
const COLONNES = [
  { k: 'family', w: '92px' },
  { k: 'brand', w: '118px' },
  { k: 'pmp', w: '76px' },
  { k: 'pv', w: '128px' },
  { k: 'value', w: '96px' },
] as const;
type ColonneOptionnelle = (typeof COLONNES)[number]['k'];

/**
 * Le mot d'une colonne facultative — le même pour l'en-tête de la liste et le panneau des colonnes.
 * ⚠️ UNE FONCTION, PAS UNE COLONNE `l` DE `COLONNES` : famille, marque et prix sont des champs du
 *    catalogue, dont le mot vient du vocabulaire du magasin ; rangé dans la constante, il serait lu à
 *    l'import, avant le contexte magasin, et l'écran dirait « Famille » chez qui l'a renommée.
 */
function columnLabel(k: ColonneOptionnelle): string {
  switch (k) {
    case 'family': return fieldLabel('family');
    case 'brand': return fieldLabel('brand');
    case 'pv': return fieldLabel('salePriceInclTax');
    case 'pmp': return 'PMP';
    case 'value': return t('Valeur PMP');
  }
}

/**
 * Les motifs, tels que le serveur les accepte — l'écran ne peut pas en inventer un.
 * ⚠️ La maquette propose des motifs libres (« Cadeau · geste commercial »…) ; le serveur ferme le
 * vocabulaire pour que le grand livre reste analysable. C'est le serveur qui a raison.
 */
const MOTIFS: { v: StockCorrectionReason; l: Translatable }[] = [
  { v: 'inventory', l: key('Inventaire') },
  { v: 'breakage', l: key('Casse') },
  { v: 'theft', l: key('Vol / démarque') },
  { v: 'manual_correction', l: key('Correction manuelle') },
];

/**
 * Le BADGE du grand livre — la nature vient du SERVEUR (`kind`, même CASE SQL que le journal des
 * mouvements) et l'habillage de `NATURES` (partagé avec `/mouvements`).
 * ⚠️ Le mapping local qui vivait ici a été SUPPRIMÉ : il supposait `retrocession` là où les
 * écrivains posent `transfer`, ignorait `reprise` — et badgeait « Correction » toute la reprise
 * (signalé utilisateur, 2026-08-28). Deux mappings = une divergence garantie (Forme A).
 */
function movementBadge(m: StockLedgerLine): { l: string; bg: string; fg: string } {
  const nat = NATURES[m.kind];
  return { l: t(nat.l).toUpperCase(), bg: nat.bg, fg: nat.fg };
}

// Le lexique FR du grand livre — PARTAGÉ avec `/mouvements` (une liste, deux écrans).
const MOUVEMENT: Record<string, Translatable> = {
  in: key('Entrée'), out: key('Sortie'), adjustment: key('Ajustement'),
  transfer_in: key('Transfert reçu'), transfer_out: key('Transfert émis'),
};

/** Le motif d’un mouvement, puis son TYPE en repli — les deux résolus, la donnée brute en dernier. */
const motifLisible = (raison: string | null | undefined, type: string): string => {
  const connu = RAISON[raison ?? ''] ?? MOUVEMENT[type];
  return connu ? t(connu) : (raison ?? type);
};

const euros = (cents: number | null): string =>
  cents == null ? '—' : formatMoney(cents, { thousandsSep: ' ' });
/** Les KPI s'affichent en euros RONDS (maquette `eur(v, 0)`) — un bandeau se lit, il ne se compte pas. */
const eurosRonds = (cents: number): string => formatMoney(cents, { thousandsSep: ' ', displayScale: 0 });

/**
 * L'ÉCHELLE FINE DU PMP — quatre décimales, celle des prix d'achat que le serveur applique
 * (`COST_FINE_SCALE` de core-inventory, dont ce paquet ne dépend pas). Nommée UNE fois pour que l'écran
 * et le fichier exporté lisent le MÊME nombre : un `4` à l'écran et un `100000` dans l'export, c'est un
 * PMP dix fois trop petit dans le CSV sous un écran qui affiche le bon.
 */
const PMP_FINE_SCALE = 4;

const dateHeure = (ms: number): string => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * LES EN-TÊTES DE L'EXPORT — le mot du MAGASIN pour chaque colonne qui est un champ du catalogue, la
 * DEVISE du magasin pour chaque colonne de montant ; les mots de document (« Quantité », « PMP »,
 * « Valeur ») restent ce qu'ils sont.
 * ⚠️ UNE FONCTION APPELÉE AU CLIC, jamais une constante de module : celle-ci figerait le mot de la
 *    plateforme avant que le contexte magasin ne soit lu. EXPORTÉE pour être ÉPROUVÉE : « mêmes
 *    en-têtes, sens inverse » — le masque d'import rattache chacune de ces colonnes à son champ, sous
 *    n'importe quel vocabulaire, et c'est un test qui le dit, pas ce commentaire.
 * ⚠️ LA DEVISE SE LIT, ELLE NE S'ÉCRIT PAS : un « (€) » en dur sous un magasin en francs suisses est
 *    l'écran qui ment — et l'en-tête doit dire ce que les cellules font (`toInputValue` suit l'échelle
 *    de la devise courante).
 */
export function stockCsvHeaders(): string[] {
  return [
    fieldLabel('gencode'), fieldLabel('articleCode'), fieldLabel('name'), fieldLabel('color'), fieldLabel('size'),
    fieldLabel('family'), fieldLabel('brand'), t('Quantité'), `${fieldLabel('salePriceInclTax')} (${currentCurrency().code})`,
    t('PMP ({code})', { code: currentCurrency().code }), t('Valeur ({code})', { code: currentCurrency().code }),
  ];
}

/** Export du relevé courant — les colonnes d'ici, la mécanique partagée (`export-csv`). */
function exporterStock(rows: StockRow[]) {
  exporterCsv(
    'stock',
    stockCsvHeaders(),
    rows.map((r) => [
      r.gencode, r.articleCode, r.name, r.color, r.size, r.family, r.brand,
      r.qty,
      // ⚠️ Une valeur inconnue reste VIDE dans le fichier — un 0 dans un tableur devient une somme fausse
      //    (`toInputValue` rend la cellule vide pour `null`).
      toInputValue(r.salePriceCents),
      // ⚠️ LE PMP EST À L'ÉCHELLE FINE, pas au centime : rendu à SES quatre décimales, par la même
      //    fonction que les autres montants — une division écrite à la main confond l'échelle fine avec
      //    une échelle de plus, et le fichier dit un PMP dix fois plus petit que l'écran.
      toInputValue(r.pmpFine, PMP_FINE_SCALE),
      toInputValue(r.valueCents),
    ]),
  );
}

/** Les tracés maquette réutilisés plusieurs fois dans l'écran. */
const ICO = {
  download: 'M12 4v12m0 0l-4-4m4 4l4-4M4 20h16',
  lock: 'M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zM8 11V7a4 4 0 0 1 8 0v4',
  search: 'M11 4a7 7 0 100 14a7 7 0 100-14M20 20l-4-4',
  columns: 'M4 4h16v16H4zM10 4v16M16 4v16',
  close: 'M6 6l12 12M18 6L6 18',
  check: 'M5 12l5 5L20 7',
} as const;

export function Stock() {
  const scope = useStoreScope();
  const ownName = scope.view?.active?.name ?? t('Ce magasin');
  // ⚠️ MÊME SEUIL QUE TOUT LE KIT (rail→tiroir, filtres→off-canvas) : un seuil propre à l'écran
  //    ferait basculer ses outils à un autre moment que le reste de la page — doctrine « une
  //    seule gouttière » (2026-08-25).
  const mobile = useMediaMax(RAIL_BREAKPOINT);

  const [page, setPage] = useState<StockPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  // ⚠️ `?fiche=<gencode>` : l'œil du journal des mouvements arrive ICI — la recherche est posée
  //    sur le gencode et la fiche s'ouvrira dès que la ligne apparaît (effet plus bas).
  const [params] = useSearchParams();
  const ficheDemandee = params.get('fiche');
  const [q, setQ] = useState(ficheDemandee ?? '');
  const [famille, setFamille] = useState<string[]>([]);
  const [marque, setMarque] = useState<string[]>([]);
  const [sansZeros, setSansZeros] = useState(false);
  const [negatifsSeuls, setNegatifsSeuls] = useState(false);
  /** `AAAA-MM-JJ` ou vide = aujourd'hui. Une date passée fait basculer l'écran en photo lecture seule. */
  const [date, setDate] = useState('');

  const [vue, setVue] = useState<Vue>('table');
  const [tri, setTri] = useState<StockSortKey | ''>('');
  const [sens, setSens] = useState<'asc' | 'desc'>('asc');
  const [hidden, setMasquees] = useState<ColonneOptionnelle[]>([]);
  const [panneauColonnes, setPanneauColonnes] = useState(false);
  // ⚠️ ÉCHAP FERME LE PANNEAU, ET LE FOCUS Y ENTRE PUIS REVIENT AU BOUTON : au seul pointeur, il ne se quittait pas au clavier.
  const colonnes = useOverlayPanel({ open: panneauColonnes, onClose: () => setPanneauColonnes(false) });

  /**
   * Les quantités SAISIES, par gencode — avec le stock d'AVANT au moment de la frappe.
   *
   * ⚠️ LE « AVANT » VOYAGE AVEC LA SAISIE, et ce n'est pas du confort. Il se déduisait auparavant de
   * la LISTE PAGINÉE (bornée à 500) : une cellule corrigée dans la GRILLE sur une référence absente
   * de cette page ne remontait alors dans aucun lot — le champ acceptait la frappe, la barre ne
   * comptait rien, et rien ne partait (§23.0, contrôle qui fait semblant).
   * ⚠️ Il ne sert QU'À détecter « saisie identique au stock » côté écran. L'écart réel est calculé
   * par le serveur contre SA photo : une valeur d'écran périmée ne peut donc pas fausser l'écriture.
   */
  const [saisies, setSaisies] = useState<Record<string, { before: number; value: string }>>({});
  const setSaisie = useCallback((gencode: string, before: number, value: string) => {
    setSaisies((p) => ({ ...p, [gencode]: { before, value } }));
  }, []);
  const [motif, setMotif] = useState<StockCorrectionReason | ''>('');
  const [note, setNote] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const [selection, setSelection] = useState<StockRow | null>(null);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const detail = useOverlayPanel({ open: selection !== null, onClose: () => setSelection(null) });
  const [ficheOuverte, setFicheOuverte] = useState(false);
  useEffect(() => {
    if (!ficheDemandee || ficheOuverte || page == null) return;
    const r = page.rows.find((x) => x.gencode === ficheDemandee);
    // ⚠️ Une seule fois : rouvrir la fiche à chaque rechargement emprisonnerait l'utilisateur qui
    //    vient de la fermer. Si le gencode n'existe pas ici, la recherche vide le DIT — rien à forcer.
    if (r) { setSelection(r); setFicheOuverte(true); }
  }, [ficheDemandee, ficheOuverte, page]);
  const [livre, setLivre] = useState<StockLedgerLine[] | null>(null);

  /**
   * Le filtre, construit UNE fois et partagé par les quatre vues.
   * ⚠️ MÉMOÏSÉ : sans `useMemo`, un nouvel objet à chaque rendu relancerait la lecture des vues en
   * boucle (leur `useEffect` en dépend) — l'écran se rechargerait sans fin, et le serveur avec.
   */
  const filtre = useMemo<StockQuery>(() => ({
    q: q.trim() || undefined,
    family: famille.length ? famille : undefined,
    brand: marque.length ? marque : undefined,
    hideZeros: sansZeros,
    negatives: negatifsSeuls,
    asOf: date || undefined,
  }), [q, famille, marque, sansZeros, negatifsSeuls, date]);

  /**
   * ⚠️ LE PASSÉ NE SE CORRIGE PAS, et le stock DISTANT non plus (maquette `stRW`). Un mouvement daté
   * d'aujourd'hui ne change pas ce qu'il y avait mardi, et un mouvement antidaté réécrirait un grand
   * livre append-only ; chez le voisin, ce serait un mouvement que personne là-bas n'aurait demandé.
   */
  const lectureSeule = date !== '' || vue === 'distant';

  /**
   * ⚠️ LE TRI N'EST PAS DANS LE FILTRE PARTAGÉ, et c'est voulu : il ne concerne que la LISTE. L'y
   * mettre relancerait la grille, le croisé et le stock distant à chaque clic d'en-tête — pour un
   * ordre dont aucun des trois ne se sert.
   */
  const requeteListe = useMemo<StockQuery>(
    () => (tri === '' ? filtre : { ...filtre, sort: tri, dir: sens }),
    [filtre, tri, sens],
  );

  /** Les lignes ACCUMULÉES par « Afficher plus » — repart de zéro à chaque changement de filtre/tri. */
  const [suite, setSuite] = useState<StockRow[]>([]);
  // ⚠️ UNE SEULE PORTE POUR LA LISTE, première page et lots suivants : un lot parti sous l'ancien filtre, revenu après la
  //    première page du nouveau, ajouterait ses lignes à une liste qui ne les a pas demandées — et le total annoncé ne
  //    décrirait plus ce qui s'affiche.
  const latest = useLatest();
  const load = useCallback(() => {
    const isLatest = latest();
    setLoading(true);
    setErreur(null);
    // Un lot en cours devient dépassé : il ne rendra plus la main, c'est la nouvelle première page qui la reprend.
    setChargementSuite(false);
    api.stock(requeteListe)
      .then((p) => { if (!isLatest()) return; setPage(p); setSuite([]); setChaineMorte(false); })
      .catch((e: unknown) => { if (isLatest()) setErreur(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (isLatest()) setLoading(false); });
  }, [requeteListe, latest]);

  /** Toutes les lignes à l'écran : la première page + ce que « Afficher plus » a ramené. */
  const lines = useMemo(() => [...(page?.rows ?? []), ...suite], [page, suite]);
  const [chargementSuite, setChargementSuite] = useState(false);
  /**
   * ⚠️ UN LOT VIDE TUE LA CHAÎNE. Le total et les lignes sont lus à des instants différents : si
   * des références disparaissent entre deux lots, l'offset dépasse le réel et le serveur rend 0
   * ligne — sans ce verrou, « total > chargé » resterait vrai et l'effet relancerait la MÊME
   * requête à l'infini. (Le plafond supprimé bornait cette tempête par accident.)
   */
  const [chaineMorte, setChaineMorte] = useState(false);
  const chargerSuite = () => {
    const isLatest = latest();
    setChargementSuite(true);
    api.stock({ ...requeteListe, offset: lines.length })
      .then((p) => {
        if (!isLatest()) return;
        if (p.rows.length === 0) setChaineMorte(true);
        setSuite((prev) => [...prev, ...p.rows]);
      })
      .catch((e: unknown) => {
        if (!isLatest()) return;
        setChaineMorte(true);
        showToast(t('Suite illisible : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err');
      })
      .finally(() => { if (isLatest()) setChargementSuite(false); });
  };

  /** Un filtre ou une recherche est-il posé ? (la DATE n'en est pas un : même univers, autre instant.) */
  const filtreActif = filtre.q != null || (filtre.family?.length ?? 0) > 0 || (filtre.brand?.length ?? 0) > 0
    || filtre.hideZeros === true || filtre.negatives === true;

  /**
   * ⚠️ UNE VUE FILTRÉE SE CHARGE EN ENTIER (décision utilisateur 2026-08-27) : « sinon on n'a pas
   * la liste complète de ce qu'on a demandé » — une vue filtrée tronquée est un mensonge par
   * omission. SANS filtre, on garde une page + « Afficher plus » : personne ne lit 30 000 lignes.
   *
   * Par LOTS ENCHAÎNÉS de `STOCK_PAGE_SIZE` — jamais une requête géante : le PMP de chaque ligne rendue se
   * rejoue du grand livre, la borne par requête est ce qui protège le serveur. Si le filtre change, la nouvelle
   * première page repart de zéro, et un lot encore en vol sous l'ancien filtre se tait (porte `latest`).
   *
   * ⚠️ SANS PLAFOND — décision utilisateur (2026-08-27) sur MESURE réelle : 12 550 lignes chargées
   * sans aucun problème sur son matériel. J'avais posé 10 000 par prudence estimée ; une mesure bat
   * une estimation. Le verrou « lot vide » (ci-dessus) borne, lui, la seule vraie tempête possible.
   */
  useEffect(() => {
    if (!filtreActif || vue !== 'table' || loading || chargementSuite || chaineMorte) return;
    if (page == null || page.total <= lines.length) return;
    chargerSuite();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- la chaîne se re-déclenche sur l'arrivée de chaque lot (lignes) ; chargerSuite est stable par construction
  }, [filtreActif, vue, loading, chargementSuite, chaineMorte, page, lines.length]);

  /**
   * ⚠️ LA VALEUR AU PMP SE SOMME SUR LES LIGNES CHARGÉES, CÔTÉ ÉCRAN. Le PMP n'existe qu'après
   * rejeu du grand livre, donc le serveur ne le calcule que pour les lignes RENDUES — avec
   * « Afficher plus », la dernière réponse ne couvre que sa tranche : c'est ici que les tranches
   * s'additionnent, et le sous-titre dit combien de références restent hors du compte.
   */
  const valeurPmp = useMemo(() => ({
    cents: lines.reduce((sum, r) => sum + (r.valueCents ?? 0), 0),
    unvalued: lines.filter((r) => r.valueCents == null).length,
    horsChamp: Math.max(0, (page?.total ?? 0) - lines.length),
  }), [lines, page]);

  // ⚠️ La recherche attend 300 ms : sans ce délai, chaque frappe déclencherait un rejeu du grand livre
  //    côté serveur — et l'écran répondrait de plus en plus lentement à mesure qu'on tape.
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  // Les colonnes masquées suivent la PERSONNE d'un magasin à l'autre — c'est un goût, pas un réglage
  // de magasin. `bo_table_prefs` porte déjà cette portée : on n'en invente pas une seconde.
  useEffect(() => {
    api.tablePrefs('stock')
      .then((p) => setMasquees((p.layout?.hidden ?? []).filter((k): k is ColonneOptionnelle => COLONNES.some((c) => c.k === k))))
      // ⚠️ Best-effort ASSUMÉ : sans préférence, on affiche TOUT. L'écran reste utilisable, et une
      //    préférence perdue ne coûte qu'un reclic — bien moins qu'un écran qui refuse de s'ouvrir.
      .catch(() => undefined);
  }, []);

  const basculerColonne = (k: ColonneOptionnelle) => {
    const suite = hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k];
    setMasquees(suite);
    void api.saveTablePrefs('stock', { layout: { order: COLONNES.map((c) => c.k), hidden: suite } })
      .catch((e: unknown) => showToast(t('Réglage non mémorisé : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'));
  };
  const visible = (k: ColonneOptionnelle) => !hidden.includes(k);

  /**
   * Clic d'en-tête : même colonne = on inverse ; nouvelle colonne = texte en croissant, CHIFFRE en
   * DÉCROISSANT (maquette : un nombre se lit d'abord du plus grand).
   */
  const trierPar = (k: StockSortKey) => {
    if (tri === k) { setSens((x) => (x === 'asc' ? 'desc' : 'asc')); return; }
    setTri(k);
    setSens(k === 'name' || k === 'family' || k === 'brand' ? 'asc' : 'desc');
  };

  useEffect(() => {
    if (!selection) { setLivre(null); return; }
    let vivant = true;
    setLivre(null);
    api.stockLedger(selection.gencode)
      .then((r) => { if (vivant) setLivre(r.rows); })
      .catch((e: unknown) => { if (vivant) showToast(t('Grand livre illisible : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'); });
    return () => { vivant = false; };
  }, [selection]);

  /**
   * Les lignes RÉELLEMENT modifiées — une saisie égale au stock affiché n'est pas une correction.
   * ⚠️ Calculées sur les SAISIES, jamais sur la page : une correction faite dans la grille porte
   * souvent sur une référence hors de la page courante, et la chercher là serait la perdre.
   */
  const enAttente = useMemo(
    () => Object.entries(saisies).flatMap(([gencode, { before, value }]) => {
      if (value === '') return [];
      const v = Number(value);
      if (!Number.isFinite(v) || Math.trunc(v) === before) return [];
      return [{ gencode, avant: before, after: Math.trunc(v) }];
    }),
    [saisies],
  );
  const sommeAttente = useMemo(() => {
    let up = 0; let dn = 0;
    for (const c of enAttente) { const d = c.after - c.avant; if (d > 0) up += d; else dn -= d; }
    return `+${up} · −${dn}`;
  }, [enAttente]);

  const save = () => {
    if (!motif || enAttente.length === 0) return;
    setEnvoi(true);
    api.correctStock(enAttente.map((c) => ({ gencode: c.gencode, targetQty: c.after })), motif, note.trim() || null)
      .then((r) => {
        showToast(
          r.written === 0
            ? t('Aucun écart à enregistrer — les quantités saisies étaient déjà les bonnes.')
            : t('{n} mouvement écrit au grand livre.', { n: r.written }),
          'ok',
        );
        setSaisies({});
        setMotif('');
        setNote('');
        load();
      })
      // ⚠️ La cause RÉELLE, pas « échec » : un refus du serveur nomme le champ fautif, et c'est ce
      //    que l'opérateur doit lire pour corriger sa saisie.
      .catch((e: unknown) => showToast(t('Correction refusée : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'))
      .finally(() => setEnvoi(false));
  };

  /** Le pas −/+ de la maquette : il déplace la CIBLE d'une pièce, à partir de la dernière saisie. */
  const bump = (r: StockRow, delta: number) => {
    const cur = saisies[r.gencode]?.value;
    const base = cur !== undefined && cur !== '' && Number.isFinite(Number(cur)) ? Math.trunc(Number(cur)) : r.qty;
    setSaisie(r.gencode, r.qty, String(base + delta));
  };

  const resume = page?.summary;
  const aujourdHui = new Date().toISOString().slice(0, 10);
  const roLabel = date !== ''
    ? t('Photo au {v} — lecture seule', { v: date.split('-').reverse().join('/') })
    : t('Stock distant — lecture seule');

  /** Les KPI de la maquette : quantité + trois valorisations, chacune disant sa base de calcul. */
  const kpis: KpiItem[] = resume == null ? [] : [
    {
      label: t('Quantité totale'), value: t('{n} pièce', { n: resume.pieces }),
      sub: t('{n} référence · vue filtrée', { n: resume.references }),
      icon: 'M3 7l9-4 9 4v10l-9 4-9-4V7zM3 7l9 4 9-4M12 11v10',
      tone: 'teal',
    },
    {
      // ⚠️ À une date passée, le serveur rejoue AUSSI le PMP à cette borne (pas seulement les
      //    quantités) : le titre peut donc dire « de l'époque » sans mentir.
      label: date ? t('Valeur au PMP de l’époque') : t('Valeur au PMP'),
      // ⚠️ Sommée sur les lignes CHARGÉES (le PMP se rejoue ligne à ligne) — le sous-titre dit
      //    combien de références restent hors du compte tant qu'on n'a pas tout affiché.
      value: eurosRonds(valeurPmp.cents),
      // ⚠️ TROIS ÉTIQUETTES INDÉPENDANTES, séparées par « · » dans le CODE : la clé ne porte jamais le séparateur.
      sub: [
        date ? t('coût rejoué à cette date') : t('coût moyen pondéré'),
        ...(valeurPmp.unvalued > 0 ? [t('{n} réf. non valorisée', { n: valeurPmp.unvalued })] : []),
        ...(valeurPmp.horsChamp > 0 ? [t('{n} réf. non chargée', { n: valeurPmp.horsChamp })] : []),
      ].join(' · '),
      icon: 'M18.5 5.5A7.5 7.5 0 0 0 13 3.5a8 8 0 0 0 0 17 7.5 7.5 0 0 0 5.5-2M3.5 10h10M3.5 14h8.5',
      tone: 'blue',
    },
    {
      label: t('Valeur au PV HT'), value: eurosRonds(resume.valueSaleHtCents), sub: t('hors remises de caisse'),
      icon: 'M3 12V4h8l10 10-8 8L3 12zM8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
      tone: 'violet',
    },
    {
      label: t('Valeur au PV TTC'), value: eurosRonds(resume.valueSaleTtcCents), sub: t('prix de vente affichés'),
      icon: 'M5 3h14v18l-2-2-2 2-2-2-2 2-2-2-2 2-2-2V3zM9 8h6M9 12h6M9 16h4',
      tone: 'amber',
    },
  ];

  /**
   * Le gabarit de colonnes de la liste — les largeurs de la maquette, colonnes masquées retirées.
   * ⚠️ Le prix de vente fait 128 px et non les 82 de la maquette : « Prix de vente TTC » — le mot unifié
   *    du catalogue, là où le dessin abrégeait « PV TTC » — ne tient pas en 82 px sans chevaucher la
   *    colonne voisine. C'est CE gabarit qui agit : la justification vit ici, pas sur `COLONNES`.
   */
  const gridT = [
    'minmax(0,1.5fr)',
    visible('family') && '92px',
    visible('brand') && '118px',
    visible('pmp') && '76px',
    visible('pv') && '128px',
    '158px',
    visible('value') && '96px',
  ].filter(Boolean).join(' ');

  /**
   * Les en-têtes, avec leur clé de tri quand le SERVEUR sait trier.
   * ⚠️ « PMP » N'EST PAS TRIABLE, et la maquette avait tort de le promettre : le PMP n'existe qu'après
   * rejeu du grand livre, jamais en SQL — un clic n'aurait rien trié (§23.0, bouton qui fait semblant).
   */
  const enTetes = [
    { l: t('Article'), k: 'name' as StockSortKey | null, jc: 'flex-start', col: null as ColonneOptionnelle | null },
    // ⚠️ CENTRÉES (demande utilisateur 2026-08-27) : collées à gauche de leurs colonnes fixes,
    //    famille et marque se lisaient dans le prolongement du libellé article.
    { l: columnLabel('family'), k: 'family' as StockSortKey | null, jc: 'center', col: 'family' as ColonneOptionnelle | null },
    { l: columnLabel('brand'), k: 'brand' as StockSortKey | null, jc: 'center', col: 'brand' as ColonneOptionnelle | null },
    { l: columnLabel('pmp'), k: null as StockSortKey | null, jc: 'flex-end', col: 'pmp' as ColonneOptionnelle | null },
    { l: columnLabel('pv'), k: 'pv' as StockSortKey | null, jc: 'flex-end', col: 'pv' as ColonneOptionnelle | null },
    { l: t('Stock'), k: 'qty' as StockSortKey | null, jc: 'center', col: null as ColonneOptionnelle | null },
    { l: columnLabel('value'), k: 'value' as StockSortKey | null, jc: 'flex-end', col: 'value' as ColonneOptionnelle | null },
  ].filter((h) => !h.col || visible(h.col));

  const negBadge = resume?.negatives ?? 0;
  const troncature = page != null && page.total > lines.length;

  // ⚠️ EN FLUX, PAS ÉPINGLÉ : la 1ʳᵉ version fixait la hauteur à `100dvh − 108px` — un chiffre qui
  //    devine le chrome du shell. Faux d'un pixel, la page portait TROIS ascenseurs (tableau + page
  //    + navigateur). Désormais la page coule (un seul défilement, celui du corps), et seul le
  //    TABLEAU défile dans sa carte, borné par le jeton du kit — exactement comme les DataTable.
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* ── L'en-tête COMMUNE du back-office : titre + sous-titre à gauche, actions à droite ──────
          ⚠️ MARGE DE 16, RELEVÉE DU DESSIN (passe de contrôle 2026-09-07). Elle valait 18, et le
          commentaire d'alors s'en justifiait par « même géométrie que Promotions/Écarts/Étiquettes » —
          or ÉCARTS est à 14. Le commentaire affirmait donc un alignement FAUX pour l'un des trois
          écrans qu'il citait, et c'est lui qui aurait fait accepter le 18 à un relecteur.
          ⚠️ IL N'Y A PAS DE VALEUR UNIQUE À CHERCHER : le dessin pose 14, 16 ou 18 selon l'écran. On
          suit le sien, jamais une moyenne ni la valeur la plus fréquente.
          La bulle « i » qui portait cette phrase est RETIRÉE (demande user 2026-08-27) : le
          sous-titre est là pour ça, comme sur les autres pages. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('État du stock')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            
            {t('Le stock ne s’écrit pas : il se déduit de ses mouvements — corriger, c’est en créer un.')}
          </p>
        </div>
        <input
          type="date"
          value={date || aujourdHui}
          max={aujourdHui}
          onChange={(e) => { const v = e.target.value; setDate(v === aujourdHui ? '' : v); setSaisies({}); setSelection(null); }}
          title={t('Voir le stock tel qu’il était à cette date')}
          style={{ height: 34, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '12.5px', fontWeight: 600, outline: 'none', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
        />
        {lectureSeule && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 11px', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-dense)', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
            <Trait d={ICO.lock} size={12} stroke={2.1} />
            {roLabel}
          </span>
        )}
        <button
          type="button"
          onClick={() => { if (lines.length > 0) { exporterStock(lines); showToast(t('{n} ligne exportée', { n: lines.length }), 'ok'); } }}
          disabled={lines.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: 7, height: 36, padding: '0 16px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap', cursor: lines.length ? 'pointer' : 'default', opacity: lines.length ? 1 : 0.5 }}
        >
          <Trait d={ICO.download} size={13} stroke={2} />
          
          {t('Export Excel')}
        </button>
      </div>

      {/* ── Les 4 repères — masqués sur le stock distant (maquette `stKpiOn`) ────────────────────── */}
      {vue !== 'distant' && resume && (
        <KpiRow items={kpis} style={{ marginBottom: 10 }} />
      )}

      {/* ── LA CARTE : barre d'outils + la vue choisie, qui défile À L'INTÉRIEUR ─────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--ink-200)' }}>
          <FilterToolbar
            title={t('Filtres du stock')}
            actifs={(famille.length > 0 ? 1 : 0) + (marque.length > 0 ? 1 : 0) + (sansZeros ? 1 : 0) + (negatifsSeuls ? 1 : 0)}
            search={
              /* ⚠️ CAPSULE `field-search` DU KIT — le même champ que le catalogue et le journal,
                  à l'identique partout (règle utilisateur 2026-08-27). */
              <div className="field-search">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
                  <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.5-4.5" />
                </svg>
                <input
                  // ⚠️ PAS `type="search"` : le navigateur y ajoute SA croix d'effacement et sa propre
                  // métrique — deux éléments que la charte ne dessine pas, dans une capsule étroite.
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  /* ⚠️ Le placeholder promet ce que le SQL tient VRAIMENT : la recherche serveur couvre
                     désignation, code article, gencode, famille et marque — vérifié par test. */
                  placeholder={t('Article, code, code-barres, famille, marque…')}
                  aria-label={t('Rechercher dans le stock')}
                />
              </div>
            }
            filters={<>
              {/* ⚠️ LES PICKERS DE LA CHARTE (recherche intégrée, MULTI, sans création — règle
                  utilisateur 2026-08-27) : les mêmes contrôles que le journal et le catalogue.
                  Les facettes viennent du serveur SANS filtre (toutes celles du magasin) — une
                  valeur déjà choisie reste donc toujours offerte, et donc révocable. */}
              <Picker
                id="bo-stock-famille" label={fieldLabel('family')} allLabel={t('Toutes les familles')} multiple
                options={(page?.families ?? []).map((v) => ({ value: v, label: v }))}
                loading={!page && loading}
                value={famille} onChange={setFamille}
              />
              <Picker
                id="bo-stock-marque" label={fieldLabel('brand')} allLabel={t('Toutes les marques')} multiple
                options={(page?.brands ?? []).map((v) => ({ value: v, label: v }))}
                loading={!page && loading}
                value={marque} onChange={setMarque}
              />
              {/* Les deux bascules VIVENT AVEC les filtres : sur mobile elles suivent dans le tiroir,
                  et le compte du bouton « Filtres » les inclut. */}
              <button
                type="button" onClick={() => setSansZeros((x) => !x)} aria-pressed={sansZeros}
                style={{ height: 36, padding: '0 11px', border: `1.5px solid ${sansZeros ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-pill)', background: sansZeros ? 'var(--teal-100)' : 'var(--paper)', color: sansZeros ? 'var(--teal-800)' : 'var(--ink-600)', fontSize: 'var(--t-dense)', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}
              >
                
                {t('Masquer les zéros')}
              </button>
              <button
                type="button" onClick={() => setNegatifsSeuls((x) => !x)} aria-pressed={negatifsSeuls}
                style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 11px', border: `1.5px solid ${negatifsSeuls ? 'var(--rose)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-pill)', background: negatifsSeuls ? 'var(--rose-bg)' : 'var(--paper)', color: negatifsSeuls ? 'var(--rose-fg)' : 'var(--ink-600)', fontSize: 'var(--t-dense)', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}
              >
                <Trans k='Négatifs <1>{negBadge}</1>' vars={{ negBadge }} parts={[(x) => <span style={{ height: 17, minWidth: 17, padding: '0 5px', borderRadius: 'var(--r-pill)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-micro)', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}>{x}</span>]} />
              </button>
              {/* « Effacer les filtres » — lien teal, rendu SEULEMENT quand un filtre est posé
                  (recette du catalogue). La DATE n'en fait pas partie : c'est une photo, pas un filtre,
                  et son chip verrou la montre déjà. */}
              {(famille.length > 0 || marque.length > 0 || sansZeros || negatifsSeuls) && (
                <button
                  type="button"
                  onClick={() => { setFamille([]); setMarque([]); setSansZeros(false); setNegatifsSeuls(false); }}
                  style={{ height: 36, padding: '0 6px', border: 'none', background: 'none', color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  
                  {t('Effacer les filtres')}
                </button>
              )}
            </>}
          />
          {/* Les outils de VUE — à droite, hors du tiroir de filtres : changer de présentation
              n'est pas filtrer. */}
          {/* ⚠️ LE SEGMENTÉ DU KIT, pas un duplicata local (règle utilisateur 2026-08-27 : ce qui est
              pertinent entre au kit pour resservir) : `fluid` lui donne sa ligne pleine largeur qui
              GLISSE sous le seuil — comportement désormais partagé par tous les écrans. `order`
              la pousse après la rangée Filtres/Colonnes — le DOM, lui, ne bouge pas. */}
          <div style={mobile ? { flexBasis: '100%', order: 1, minWidth: 0 } : { marginLeft: 'auto', flexShrink: 0 }}>
            <Segmented
              label={t('Présentation du stock')} fluid
              options={VUES.map((v) => ({ id: v.v, label: t(v.l) }))}
              value={vue}
              onChange={(v) => setVue(v)}
            />
          </div>
          {/* ⚠️ Le sélecteur de colonnes n'apparaît QUE sur la liste : la maquette le laissait aussi
              sur le stock distant, où il n'aurait RIEN piloté — un bouton sans effet (§23.0). */}
          {vue === 'table' && (
            <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, marginLeft: mobile ? 'auto' : undefined }}>
              <button
                type="button" onClick={() => setPanneauColonnes((o) => !o)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 12px', border: `1px solid ${hidden.length ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: hidden.length ? 'var(--teal-100)' : 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}
              >
                <Trait d={ICO.columns} size={14} stroke={1.8} />
                
                {hidden.length ? t('Colonnes · {n} masquée', { n: hidden.length }) : t('Colonnes')}
              </button>
              {panneauColonnes && (
                <>
                  <div onClick={() => setPanneauColonnes(false)} style={{ position: 'fixed', inset: 0, zIndex: 590 }} />
                  <div ref={colonnes.panelRef} role="dialog" aria-label={t('Colonnes affichées')} style={{ position: 'absolute', right: 0, top: 42, zIndex: 600, width: 274, background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: '0 18px 44px -12px rgba(12,14,16,.28)', overflow: 'hidden', animation: 'pop .15s cubic-bezier(.2,.7,.3,1)' }}>
                    <div style={{ padding: '11px 14px 5px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Colonnes affichées')}</div>
                    <div style={{ padding: '2px 6px 6px' }}>
                      {/* Les colonnes VERROUILLÉES d'abord — grisées, la maquette les montre pour dire
                          qu'elles existent et qu'on n'y touche pas. */}
                      {[{ l: t('Article') }, { l: t('Stock') }].map((c) => (
                        <div key={c.l} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 8px', borderRadius: 'var(--r-xs)', cursor: 'default' }}>
                          <span style={{ width: 17, height: 17, borderRadius: 'var(--r-xs)', border: '1.5px solid var(--ink-300)', background: 'var(--ink-300)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Trait d={ICO.check} size={11} stroke={3} /></span>
                          <span style={{ flex: 1, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-400)' }}>{c.l}</span>
                        </div>
                      ))}
                      {COLONNES.map((c) => {
                        const on = visible(c.k);
                        return (
                          <div key={c.k} role="button" tabIndex={0} onClick={() => basculerColonne(c.k)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); basculerColonne(c.k); } }} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 8px', borderRadius: 'var(--r-xs)', cursor: 'pointer' }}>
                            <span style={{ width: 17, height: 17, borderRadius: 'var(--r-xs)', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`, background: on ? 'var(--teal-500)' : 'var(--paper)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on ? <Trait d={ICO.check} size={11} stroke={3} /> : null}</span>
                            <span style={{ flex: 1, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-800)' }}>{columnLabel(c.k)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ padding: '8px 14px 10px', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
                      
                      {t('Visibilité — mémorisée sur votre profil (tous vos postes), par tableau')}
                    </div>
                  </div>
                </>
              )}
            </span>
          )}
        </div>

        {/* ── Le corps qui DÉFILE ─────────────────────────────────────────────────────────────────── */}
        {/* ⚠️ `position: relative` N'EST PAS DÉCORATIF — c'est LE correctif de l'ascenseur fantôme,
            mesuré au CDP (2026-08-27) : des éléments en `position: absolute` vivent dans les lignes
            (Copyable), et un conteneur de défilement STATIQUE n'est pas leur bloc conteneur — ils se
            rattachaient au VIEWPORT, étirant la racine à 21 000 px : barre du navigateur géante,
            vide sous la page, rail qui défile. La recette maison `.dt-scroll` porte ce même
            `position:relative` — on l'avait manqué en recopiant la maquette. */}
        <div style={vue === 'table' ? { maxHeight: TABLE.scroll.maxHeight, overflow: 'auto', position: 'relative' } : undefined}>
          {vue === 'grille' ? (
            <GrilleDesTailles filtre={filtre} saisies={saisies} setSaisie={setSaisie} lectureSeule={lectureSeule} />
          ) : vue === 'pivot' ? (
            <TableauCroise filtre={filtre} />
          ) : vue === 'distant' ? (
            <StockDistant filtre={filtre} ownName={ownName} />
          ) : erreur ? (
            <div style={{ padding: '30px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--rose-fg)' }}>{t('Stock illisible')}</div>
              <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3 }}>{t('Le magasin n’a pas répondu : {erreur}', { erreur })}</div>
              <button type="button" onClick={load} style={{ marginTop: 12, height: 34, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>{t('Réessayer')}</button>
            </div>
          ) : loading && !page ? (
            <div style={{ padding: 30, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture du stock…')}</div>
          ) : (
            <div>
              {/* L'en-tête COLLANT de la liste — clic = tri serveur, flèche sur la colonne active. */}
              <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'grid', gridTemplateColumns: gridT, gap: 10, padding: '9px 16px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
                {/* ⚠️ La clé est la COLONNE, jamais son mot : le commerçant peut nommer deux champs pareil. */}
                {enTetes.map((h) => (
                  h.k == null ? (
                    <div key={h.col ?? h.k} style={{ display: 'inline-flex', justifyContent: h.jc, whiteSpace: 'nowrap', minWidth: 0 }}>{h.l}</div>
                  ) : (
                    <button
                      key={h.col ?? h.k} type="button" title={t('Trier')} onClick={() => trierPar(h.k!)}
                      style={{ border: 'none', background: 'transparent', padding: 0, margin: 0, font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', color: tri === h.k ? 'var(--teal-700)' : 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: h.jc, whiteSpace: 'nowrap', minWidth: 0 }}
                    >
                      {h.l}
                      <span style={{ fontSize: 'var(--t-eyebrow)' }}>{tri === h.k ? (sens === 'asc' ? '▲' : '▼') : ''}</span>
                    </button>
                  )
                ))}
              </div>

              {lines.map((r) => {
                const saisie = saisies[r.gencode]?.value ?? '';
                const target = saisie !== '' && Number.isFinite(Number(saisie)) ? Math.trunc(Number(saisie)) : r.qty;
                const pend = target - r.qty;
                const shown = target;
                const rw = !lectureSeule;
                return (
                  <div
                    key={r.gencode}
                    onClick={() => setSelection(r)}
                    style={{ display: 'grid', gridTemplateColumns: gridT, gap: 10, padding: '0 16px', minHeight: 42, alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-lead)', cursor: 'pointer', background: shown < 0 ? 'var(--rose-bg)' : 'var(--paper)' }}
                  >
                    <div style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 7 }}>
                      {/* ⚠️ LE NOM EST LE BOUTON QUI OUVRE LE DÉTAIL, COMME LA LIGNE : un `div` cliquable n'existe pas au
                          clavier, et les autres contrôles de la ligne (copier le gencode, retirer ou ajouter une pièce)
                          font autre chose. Il porte son geste — vivre par la remontée jusqu'à la ligne serait un bouton
                          mort en sursis. */}
                      <button
                        type="button" aria-haspopup="dialog" onClick={(e) => { e.stopPropagation(); setSelection(r); }}
                        style={{ minWidth: 0, padding: 0, border: 'none', background: 'transparent', font: 'inherit', textAlign: 'left', cursor: 'pointer', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {r.name}{r.color ? ` · ${r.color}` : ''}{r.size ? ` · ${r.size}` : ''}
                      </button>
                      <Copyable value={r.gencode} label={t('Copier le gencode {gencode}', { gencode: r.gencode })} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', color: 'var(--ink-400)', whiteSpace: 'nowrap', flexShrink: 0, padding: 0 }}>{r.gencode}</Copyable>
                    </div>
                    {visible('family') && (
                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 'var(--r-r3)', background: familyDot(r.family), flexShrink: 0 }} />
                        <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.family ?? '—'}</span>
                      </div>
                    )}
                    {visible('brand') && (
                      <div style={{ minWidth: 0, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>{r.brand ?? '—'}</div>
                    )}
                    {/* ⚠️ `—` et non « 0,00 € » : une référence non valorisable n'a pas une valeur nulle,
                        elle n'a pas de valeur connue. Les confondre fausserait toute lecture de marge. */}
                    {visible('pmp') && (
                      <div style={{ minWidth: 0, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>
                        {r.pmpFine == null ? '—' : euros(rescale(r.pmpFine, PMP_FINE_SCALE, 2))}
                      </div>
                    )}
                    {visible('pv') && (
                      <div style={{ minWidth: 0, textAlign: 'right', fontWeight: 600, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
                        {euros(r.salePriceCents)}
                      </div>
                    )}
                    <div style={{ minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                      {rw ? (
                        // ⚠️ Le pas et la saisie ne doivent PAS ouvrir le grand livre : le clic s'arrête ici.
                        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <button type="button" aria-label={t('Retirer une pièce — {v}', { v: `${r.name}${r.color ? ` · ${r.color}` : ''}${r.size ? ` · ${r.size}` : ''}` })} onClick={() => bump(r, -1)} style={{ width: 26, height: 26, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-control)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>−</button>
                          <input
                            aria-label={t('Quantité en stock — {v}', { v: `${r.name}${r.color ? ` · ${r.color}` : ''}${r.size ? ` · ${r.size}` : ''}` })}
                            value={saisie === '' ? String(r.qty) : saisie}
                            onChange={(e) => setSaisie(r.gencode, r.qty, e.target.value.replace(/[^\d-]/g, ''))}
                            inputMode="numeric"
                            style={{ width: 48, height: 26, textAlign: 'center', border: `1px solid ${pend !== 0 ? 'var(--amber)' : shown < 0 ? 'var(--rose)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-xs)', background: pend !== 0 ? 'var(--amber-bg)' : 'var(--paper)', color: shown < 0 ? 'var(--rose-fg)' : 'var(--ink-900)', fontSize: '12.5px', fontWeight: 700, outline: 'none', fontVariantNumeric: 'tabular-nums' }}
                          />
                          <button type="button" aria-label={t('Ajouter une pièce — {v}', { v: `${r.name}${r.color ? ` · ${r.color}` : ''}${r.size ? ` · ${r.size}` : ''}` })} onClick={() => bump(r, +1)} style={{ width: 26, height: 26, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-control)', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>+</button>
                          {pend !== 0 && (
                            <span style={{ height: 17, padding: '0 5px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '9.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', fontVariantNumeric: 'tabular-nums' }}>{pend > 0 ? `+${pend}` : pend}</span>
                          )}
                        </span>
                      ) : (
                        <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: r.qty < 0 ? 'var(--rose-fg)' : 'var(--ink-900)' }}>{r.qty}</span>
                      )}
                    </div>
                    {visible('value') && (
                      <div style={{ minWidth: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--ink-900)' }}>{euros(r.valueCents)}</div>
                    )}
                  </div>
                );
              })}

              {lines.length === 0 && (
                <div style={{ padding: '26px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucune référence ne correspond')}</div>
                  <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3 }}>{t('Retirez un filtre — un stock vide ne se cache jamais derrière un filtre oublié.')}</div>
                </div>
              )}

              {/* Le pied : le compte, ce que couvre l'export, et la SUITE à charger. La borne de 500
                  est PAR REQUÊTE (le PMP de chaque ligne rendue se rejoue du grand livre — sans
                  borne, une frappe rejouerait tout le magasin), plus par écran : « Afficher plus »
                  ramène les 500 suivantes, autant de fois qu'il en reste. */}
              {lines.length > 0 && (
                <div style={{ position: 'sticky', bottom: 0, zIndex: 2, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)' }}>
                  <span style={{ fontSize: 'var(--t-dense)', color: troncature ? 'var(--amber-fg)' : 'var(--ink-600)' }}>
                    {troncature
                      ? t('{n} références affichées sur {total} — l’export reprend les lignes affichées', { n: lines.length, total: nombre(page!.total) })
                      // ⚠️ DEUX PHRASES ENTIÈRES, et le compte en NOMBRE : la clause « vue filtrée
                      //    COMPLÈTE » était un trou de plus, donc un fragment livré sans sa phrase ; et
                      //    `Intl.PluralRules` choisit sur un NOMBRE, jamais sur « 1 234 ».
                      : filtreActif
                        ? t('{n} référence · vue filtrée COMPLÈTE — l’export reprend exactement ces lignes', { n: lines.length })
                        : t('{n} référence — l’export reprend exactement ces lignes', { n: lines.length })}
                    {negBadge > 0 && !negatifsSeuls ? <>{' · '}{t('{n} stock négatif affiché', { n: negBadge })}</> : ''}
                  </span>
                  {troncature && filtreActif && !chaineMorte && (
                    <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                      {t('Chargement de la suite… {v} / {v2}', { v: lines.length.toLocaleString('fr-FR'), v2: page!.total.toLocaleString('fr-FR') })}
                    </span>
                  )}
                  {troncature && filtreActif && chaineMorte && (
                    /* ⚠️ La chaîne s'est arrêtée SANS avoir tout : on le DIT (lot vide ou en échec —
                        des lignes ont pu bouger pendant le chargement). Relancer = re-filtrer. */
                    <span style={{ fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', whiteSpace: 'nowrap' }}>
                      {t('Chargement interrompu à {v} — modifiez le filtre pour relancer', { v: lines.length.toLocaleString('fr-FR') })}</span>
                  )}
                  {troncature && !filtreActif && (
                    <button
                      type="button" onClick={chargerSuite} disabled={chargementSuite}
                      style={{ height: 30, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: chargementSuite ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {chargementSuite ? t('Chargement…') : t('Afficher les {n} suivantes', { n: Math.min(STOCK_PAGE_SIZE, page!.total - lines.length) })}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── La barre STICKY des corrections en attente — motif OBLIGATOIRE, sinon rien ne part ──── */}
      {enAttente.length > 0 && !lectureSeule && (
        <div style={{ position: 'sticky', bottom: 14, marginTop: 14, background: 'var(--paper)', border: '1.5px solid var(--amber)', borderRadius: 'var(--r-md)', boxShadow: '0 12px 34px -8px rgba(0,0,0,.22)', padding: '13px 16px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', zIndex: 50 }}>
          <span style={{ display: 'flex', color: 'var(--amber-fg)', flexShrink: 0 }}><Trait d="M12 4v12m0 0l-4-4m4 4l4-4" size={17} stroke={2} /></span>
          <span style={{ minWidth: 150 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>
              {t('{n} correction en attente', { n: enAttente.length })}
            </span>
            <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-600)' }}>{t('{sommeAttente} — chaque ligne deviendra un mouvement', { sommeAttente })}</span>
          </span>
          <select
            aria-label={t('Motif')}
            value={motif}
            onChange={(e) => setMotif(e.target.value as StockCorrectionReason | '')}
            style={{ height: 38, padding: '0 11px', border: `1px solid ${motif ? 'var(--ink-200)' : 'var(--amber)'}`, borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', outline: 'none', flexShrink: 0 }}
          >
            <option value="">{t('Motif — obligatoire')}</option>
            {MOTIFS.map((m) => <option key={m.v} value={m.v}>{t(m.l)}</option>)}
          </select>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('Précision (facultatif)')}
            style={{ flex: 1, minWidth: 150, height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', outline: 'none' }}
          />
          <button
            type="button" onClick={save} disabled={!motif || envoi}
            style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: motif && !envoi ? 'var(--ink-900)' : 'var(--ink-200)', color: motif && !envoi ? 'var(--paper)' : 'var(--ink-400)', fontSize: 'var(--t-lead)', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap', cursor: motif && !envoi ? 'pointer' : 'default' }}
          >
            {envoi ? t('Enregistrement…') : t('Enregistrer les mouvements')}
          </button>
          <button
            type="button" onClick={() => { setSaisies({}); setMotif(''); setNote(''); }} disabled={envoi}
            style={{ height: 38, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 600, flexShrink: 0, cursor: 'pointer' }}
          >
            
            {t('Annuler')}
          </button>
        </div>
      )}

      {/* ── Le GRAND LIVRE d'une référence — panneau latéral droit, par-dessus l'écran ──────────── */}
      {selection && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(12,14,16,.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: 18 }}>
          <div onClick={() => setSelection(null)} style={{ position: 'absolute', inset: 0 }} />
          <div ref={detail.panelRef} role="dialog" aria-modal="true" aria-label={selection.name} style={{ position: 'relative', width: 520, maxWidth: '94vw', height: '100%', background: 'var(--paper)', borderRadius: 'var(--r-lg)', boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'pop .2s cubic-bezier(.2,.7,.3,1)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--ink-200)', flexShrink: 0 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: '15.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{selection.name}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 3 }}>
                  <Copyable value={selection.gencode} label={t('Copier le gencode {gencode}', { gencode: selection.gencode })} style={{ padding: 0, font: 'inherit', color: 'inherit' }}>{selection.gencode}</Copyable>
                  {selection.articleCode ? (
                    <>
                      <span aria-hidden>·</span>
                      <Copyable value={selection.articleCode} label={t('Copier le code article {articleCode}', { articleCode: selection.articleCode })} style={{ padding: 0, font: 'inherit', color: 'inherit' }}>{selection.articleCode}</Copyable>
                    </>
                  ) : null}
                  <span>{['', selection.color, selection.size, selection.family, selection.brand].filter(Boolean).join(' · ')}</span>
                </span>
              </span>
              <span style={{ textAlign: 'right', flexShrink: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-h2)', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: selection.qty < 0 ? 'var(--rose-fg)' : 'var(--ink-900)' }}>{selection.qty}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('en stock · PMP')} {selection.pmpFine == null ? '—' : euros(rescale(selection.pmpFine, PMP_FINE_SCALE, 2))}</span>
              </span>
              <button
                type="button" onClick={() => setSelection(null)} aria-label={t('Fermer')}
                style={{ width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0, cursor: 'pointer' }}
              >
                <Trait d={ICO.close} size={15} stroke={2.4} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {livre == null ? (
                <div style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture du grand livre…')}</div>
              ) : livre.length === 0 ? (
                <div style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Aucun mouvement enregistré pour cette référence.')}</div>
              ) : (
                livre.map((m) => {
                  const badge = movementBadge(m);
                  return (
                    <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '84px minmax(0,1fr) 44px 54px', gap: 9, padding: '10px 0', alignItems: 'start', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px' }}>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{dateHeure(m.createdAt)}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', height: 19, padding: '0 6px', borderRadius: 'var(--r-xs)', background: badge.bg, color: badge.fg, fontSize: 'var(--t-eyebrow)', fontWeight: 800, letterSpacing: '.03em', whiteSpace: 'nowrap', marginTop: 4 }}>{badge.l}</span>
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', color: 'var(--ink-800)', fontWeight: 600, lineHeight: 1.4 }}>
                          {motifLisible(m.reason, m.type)}{m.note ? ` — ${m.note}` : ''}
                        </span>
                        <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 2 }}>{t('par {qui}', { qui: m.by })}</span>
                      </span>
                      <span style={{ textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: m.qty > 0 ? 'var(--teal-700)' : 'var(--ink-600)', paddingTop: 1 }}>
                        {m.qty > 0 ? `+${m.qty}` : `−${Math.abs(m.qty)}`}
                      </span>
                      <span style={{ textAlign: 'right', fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', paddingTop: 2 }}>
                        {m.qtyAfter == null ? '' : `→ ${m.qtyAfter}`}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            <div style={{ padding: '13px 20px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.6, flexShrink: 0 }}>
              <Trans k='<1>Rien ne s’efface.</1> Le stock affiché est la somme exacte de ces lignes — corriger le stock ajoute une ligne, ne retouche jamais les anciennes.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
          </div>
        </div>
      )}
    </div>
  );
}
